/**
 * Cart Flash Offer — v13.
 *
 * Visibility contract
 * -------------------
 *  - The offer is NOT shown on page load. It stays hidden until the shopper
 *    either adds something to the cart or opens the cart drawer themselves —
 *    including on an empty cart. That first interaction "activates" it and
 *    starts the countdown.
 *  - Once it has run and expired it is done for the rest of the DAY, not just
 *    the tab. State lives in localStorage keyed by the local date, so opening a
 *    new tab cannot resurrect it. A new day resets it.
 *  - On expiry it removes its own discount code, hides itself, and asks the
 *    other drawer modules to open so the drawer does not collapse into a gap.
 *
 * Why localStorage and not sessionStorage: sessionStorage is per-tab, so a new
 * tab would have shown the offer again — exactly what this is meant to prevent.
 *
 * Everything that must outlive a re-render lives in storage or on `window`,
 * never on the instance: Dawn replaces `cart-drawer-items` (quantity/remove) and
 * `#CartDrawer` (add) wholesale, so this element is destroyed and rebuilt often.
 */
(function () {
  if (window.CartFlashOfferLoaded) return;
  window.CartFlashOfferLoaded = true;

  var routes = window.routes || {};
  var UPDATE_URL = routes.cart_update_url || '/cart/update.js';
  var ADD_URL = routes.cart_add_url || '/cart/add.js';

  var DEFAULT_GAP = 12;
  var URGENT_MS = 60000;

  function request(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: body,
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error((data && (data.description || data.message)) || 'Request failed');
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  /**
   * Update text WITHOUT producing a childList mutation.
   *
   * `el.textContent = x` REPLACES the child text node, so it registers as a
   * childList change. GoKwik re-enables its checkout button from a
   * MutationObserver on <body> watching childList, debounced by 700ms — this
   * countdown ticking once a second kept resetting that debounce, so after a
   * cart re-render replaced the button it was never re-enabled and sat showing
   * its spinner. Writing through an existing text node's nodeValue is a
   * characterData mutation, which that observer does not watch.
   */
  function writeText(el, text) {
    if (!el) return;
    var node = el.firstChild;
    if (!node || node.nodeType !== 3 || el.childNodes.length !== 1) {
      el.textContent = '';
      el.appendChild(document.createTextNode(text));
      return;
    }
    if (node.nodeValue !== text) node.nodeValue = text;
  }

  function discountBody(code) {
    var form = new FormData();
    form.append('discount', code);
    return form;
  }

  function addBody(variantId) {
    var form = new FormData();
    form.append('id', variantId);
    form.append('quantity', '1');
    return form;
  }

  /**
   * Ask Dawn to re-render the drawer.
   *
   * `PUB_SUB_EVENTS` is a top-level `const` in assets/constants.js, so it lives
   * in the global LEXICAL environment and is NOT on `window` — guarding on
   * `window.PUB_SUB_EVENTS` silently skipped the publish and left the Add button
   * spinning forever. `publish` is a function declaration, so it does land on
   * window; that asymmetry is what hid the bug.
   */
  function publishCartUpdate() {
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
    var send = typeof publish === 'function' ? publish : null;

    if (events && send) {
      send(events.cartUpdate, { source: 'cart-flash-offer' });
      return true;
    }
    var items = document.querySelector('cart-drawer-items');
    if (items && typeof items.onCartUpdate === 'function') {
      items.onCartUpdate();
      return true;
    }
    return false;
  }

  /* ---------------------------------------------------------------- *
   * Once-per-day state (localStorage, so new tabs respect it)
   * ---------------------------------------------------------------- */
  function todayKey() {
    var d = new Date();
    return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
  }

  function storeKey(code, minutes) {
    return 'cn:flash:' + code + ':' + minutes;
  }

  function readState(code, minutes) {
    try {
      var raw = JSON.parse(localStorage.getItem(storeKey(code, minutes)));
      if (!raw || typeof raw !== 'object') return null;
      /* A new day wipes the slate. */
      if (raw.day !== todayKey()) return null;
      return raw;
    } catch (e) {
      return null;
    }
  }

  function writeState(code, minutes, state) {
    try {
      state.day = todayKey();
      localStorage.setItem(storeKey(code, minutes), JSON.stringify(state));
    } catch (e) {
      /* Private mode — the offer degrades to per-page behaviour. */
    }
  }

  if (customElements.get('cart-flash-offer')) return;

  customElements.define(
    'cart-flash-offer',
    class CartFlashOffer extends HTMLElement {
      connectedCallback() {
        this.code = this.getAttribute('data-code') || '';
        this.minutes = parseInt(this.getAttribute('data-minutes') || '5', 10) || 5;
        this.isApplied = this.getAttribute('data-applied') === 'true';

        this.module = this.closest('[data-cn-module]');

        /* The countdown moved up into the <summary> so the heading and timer
           share one line, which puts it OUTSIDE this element — resolve it from
           the module, not from `this`. */
        var scope = this.module || this;
        this.digitsEl = scope.querySelector('[data-timer-digits]');
        this.barEl = scope.querySelector('[data-timer-bar]');
        this.errorEl = this.querySelector('[data-error-text]');

        this.isPrimary = !window.__cnFlashOwner || !window.__cnFlashOwner.isConnected;
        if (this.isPrimary) window.__cnFlashOwner = this;

        this._onClick = this._handleClick.bind(this);
        this.addEventListener('click', this._onClick);

        this._initCarousel();
        this._boot();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        if (this._interval) {
          clearInterval(this._interval);
          this._interval = null;
        }
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }
        if (this._drawerObserver) {
          this._drawerObserver.disconnect();
          this._drawerObserver = null;
        }
        if (window.__cnFlashOwner === this) window.__cnFlashOwner = null;
      }

      /* -------------------------------------------------------------- *
       * Visibility
       * -------------------------------------------------------------- */
      _setHidden(hidden) {
        var target = this.module || this;
        target.hidden = hidden;
        if (hidden && this.module) this.module.open = false;
      }

      _boot() {
        if (!this.code) return;

        var state = readState(this.code, this.minutes);

        /* Already had its run today — never show again until tomorrow. */
        if (state && state.done) {
          this._setHidden(true);
          return;
        }

        if (state && state.deadline) {
          if (Date.now() >= state.deadline) {
            this._expire();
            return;
          }
          /* Mid-countdown, e.g. a drawer re-render or another tab. */
          this._run(state.deadline);
          return;
        }

        /* Not activated yet: stay out of the way until the shopper adds
           something or opens the drawer. */
        this._setHidden(true);
        this._watchForActivation();
      }

      /** Activation = an add to cart, or the shopper opening the drawer. */
      _watchForActivation() {
        var self = this;
        var drawer = document.querySelector('cart-drawer');

        function activate() {
          if (self._activated) return;
          /* Re-check: another tab may have consumed it in the meantime. */
          var current = readState(self.code, self.minutes);
          if (current && current.done) {
            self._setHidden(true);
            return;
          }
          self._activated = true;

          var deadline = current && current.deadline
            ? current.deadline
            : Date.now() + self.minutes * 60000;
          writeState(self.code, self.minutes, { deadline: deadline, done: false });

          self._setHidden(false);
          if (self.module) self.module.open = true;
          self._run(deadline);
        }

        this._activate = activate;

        if (drawer) {
          if (drawer.classList.contains('active')) {
            activate();
            return;
          }
          this._drawerObserver = new MutationObserver(function () {
            if (drawer.classList.contains('active')) activate();
          });
          this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }

        /* Adding to cart opens the drawer anyway, but subscribe as a backstop
           for themes/paths that add without opening it. */
        var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
        var sub = typeof subscribe === 'function' ? subscribe : null;
        if (events && sub) sub(events.cartUpdate, activate);
      }

      /* -------------------------------------------------------------- *
       * Countdown
       * -------------------------------------------------------------- */
      _run(deadline) {
        this._deadline = deadline;
        this._span = this.minutes * 60000;
        this._setHidden(false);

        this._tick();
        this._interval = setInterval(this._tick.bind(this), 1000);
      }

      _tick() {
        var remaining = Math.max(0, this._deadline - Date.now());
        var minutes = Math.floor(remaining / 60000);
        var seconds = Math.floor((remaining % 60000) / 1000);

        writeText(
          this.digitsEl,
          (minutes < 10 ? '0' + minutes : minutes) + ':' + (seconds < 10 ? '0' + seconds : seconds),
        );
        if (this.barEl) {
          var width = (remaining / this._span) * 100 + '%';
          if (this.barEl.style.width !== width) this.barEl.style.width = width;
        }
        this.classList.toggle('cfo--urgent', remaining > 0 && remaining <= URGENT_MS);

        if (remaining <= 0) {
          clearInterval(this._interval);
          this._interval = null;
          this._expire();
        }
      }

      /**
       * Offer over: burn it for the day, hide the whole module, drop our own
       * discount code, and hand the space back to the other modules.
       */
      _expire() {
        writeState(this.code, this.minutes, { deadline: 0, done: true });
        this._setHidden(true);

        /* Let the drawer controller re-open the other modules so the drawer
           does not just lose a block and leave a gap. */
        document.dispatchEvent(new CustomEvent('cn:flash-expired', { bubbles: true }));

        if (!this.isPrimary || !this.code || !this.isApplied) return;

        request(UPDATE_URL, discountBody(''))
          .then(publishCartUpdate)
          .catch(function () {});
      }

      /* -------------------------------------------------------------- *
       * Add to cart
       * -------------------------------------------------------------- */
      _handleClick(event) {
        var button = event.target.closest('[data-add-flash]');
        if (!button || button.disabled) return;

        event.preventDefault();
        event.stopPropagation();

        var variantId = button.getAttribute('data-add-flash');
        if (!variantId) return;

        var self = this;
        button.disabled = true;
        button.classList.add('is-loading');
        this._clearError();

        var settle = function () {
          button.disabled = false;
          button.classList.remove('is-loading');
        };

        request(ADD_URL, addBody(variantId))
          .then(function () {
            return request(UPDATE_URL, discountBody(self.code));
          })
          .then(function () {
            var refreshed = publishCartUpdate();
            settle();
            if (!refreshed) window.location.reload();
          })
          .catch(function (error) {
            settle();
            self._showError(error && error.message);
          });
      }

      _showError(message) {
        this.classList.add('is-error');
        if (this.errorEl) {
          this.errorEl.textContent = message || 'Could not add this item. Please try again.';
        }
      }

      _clearError() {
        this.classList.remove('is-error');
        if (this.errorEl) this.errorEl.textContent = '';
      }

      /* -------------------------------------------------------------- *
       * Carousel
       * -------------------------------------------------------------- */
      _initCarousel() {
        this.viewport = this.querySelector('[data-viewport]');
        this.track = this.querySelector('[data-track]');
        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
        this.prevBtn = this.querySelector('[data-prev]');
        this.nextBtn = this.querySelector('[data-next]');
        this.dotsEl = this.querySelector('[data-dots]');
        this.navEl = this.querySelector('[data-nav]');

        if (!this.track || !this.viewport) return;

        this.count = this.slides.length;
        this.index = 0;

        if (this.count <= 1) {
          if (this.navEl) this.navEl.hidden = true;
          return;
        }

        this._buildDots();

        var self = this;
        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            self._goTo(self.index - 1);
          });
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            self._goTo(self.index + 1);
          });
        }

        this._bindSwipe();
        this._goTo(0);

        if (window.ResizeObserver) {
          this._resizeObserver = new ResizeObserver(function () {
            self._goTo(self.index);
          });
          this._resizeObserver.observe(this.viewport);
        }
      }

      _buildDots() {
        if (!this.dotsEl) return;
        this.dotsEl.innerHTML = '';
        this.dots = [];

        var self = this;
        this.slides.forEach(function (slide, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cfo__dot';
          dot.setAttribute('aria-label', 'Show offer ' + (i + 1) + ' of ' + self.count);
          dot.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            self._goTo(i);
          });
          self.dotsEl.appendChild(dot);
          self.dots.push(dot);
        });
      }

      _slidePitch() {
        if (!this.slides.length) return 0;
        var gap = parseFloat(getComputedStyle(this.track).columnGap);
        if (!isFinite(gap)) gap = DEFAULT_GAP;
        return this.slides[0].getBoundingClientRect().width + gap;
      }

      _goTo(target) {
        if (!this.track || this.count <= 1) return;

        this.index = Math.max(0, Math.min(this.count - 1, target));
        this.track.style.transform = 'translateX(-' + this.index * this._slidePitch() + 'px)';

        if (this.dots) {
          var current = this.index;
          this.dots.forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === current);
            dot.setAttribute('aria-current', i === current ? 'true' : 'false');
          });
        }
        if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index >= this.count - 1;
      }

      _bindSwipe() {
        var self = this;
        var startX = 0;
        var deltaX = 0;
        var dragging = false;
        var moved = false;

        this.viewport.addEventListener('pointerdown', function (e) {
          if (e.target.closest('button, a')) return;
          startX = e.clientX;
          deltaX = 0;
          moved = false;
          dragging = true;
          self.track.classList.add('is-dragging');
        });

        this.viewport.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          deltaX = e.clientX - startX;
          if (Math.abs(deltaX) > 5) moved = true;
          if (moved && e.cancelable) e.preventDefault();
          self.track.style.transform =
            'translateX(' + (-self.index * self._slidePitch() + deltaX) + 'px)';
        });

        function end() {
          if (!dragging) return;
          dragging = false;
          self.track.classList.remove('is-dragging');
          if (!moved) return self._goTo(self.index);
          if (deltaX < -40) return self._goTo(self.index + 1);
          if (deltaX > 40) return self._goTo(self.index - 1);
          self._goTo(self.index);
        }

        this.viewport.addEventListener('pointerup', end);
        this.viewport.addEventListener('pointercancel', end);
        this.viewport.addEventListener('pointerleave', end);
      }
    },
  );
})();
